import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type BotEventEntry<T> = {
  payload: T
  seq: number
}

export type BotEventsState = {
  model: BotEventEntry<object>
  overlay: BotEventEntry<object>
  theme: BotEventEntry<object>
  config: BotEventEntry<object>
  screenSize: BotEventEntry<number>
  modalHidden: BotEventEntry<null>
}

export const initialBotEventsState: BotEventsState = {
  model: { payload: {}, seq: 0 },
  overlay: { payload: {}, seq: 0 },
  theme: { payload: {}, seq: 0 },
  config: { payload: {}, seq: 0 },
  screenSize: { payload: 0, seq: 0 },
  modalHidden: { payload: null, seq: 0 },
}

const nextEntry = <T>(current: BotEventEntry<T>, payload: T): BotEventEntry<T> => ({
  payload,
  seq: current.seq + 1,
})

const botEventsSlice = createSlice({
  name: 'botEvents',
  initialState: initialBotEventsState,
  reducers: {
    modelUpdated(state, action: PayloadAction<object>) {
      state.model = nextEntry(state.model, action.payload)
    },
    overlayUpdated(state, action: PayloadAction<object>) {
      state.overlay = nextEntry(state.overlay, action.payload)
    },
    overlayReset(state) {
      state.overlay = { payload: {}, seq: 0 }
    },
    themeUpdated(state, action: PayloadAction<object>) {
      state.theme = nextEntry(state.theme, action.payload)
    },
    configUpdated(state, action: PayloadAction<object>) {
      state.config = nextEntry(state.config, action.payload)
    },
    screenSizeChanged(state, action: PayloadAction<number>) {
      state.screenSize = nextEntry(state.screenSize, action.payload)
    },
    modalHidden(state) {
      state.modalHidden = nextEntry(state.modalHidden, null)
    },
  },
})

export const {
  modelUpdated,
  overlayUpdated,
  overlayReset,
  themeUpdated,
  configUpdated,
  screenSizeChanged,
  modalHidden,
} = botEventsSlice.actions

export const selectBotEvents = (state: { botEvents: BotEventsState }) => state.botEvents

export default botEventsSlice.reducer
